import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Lock, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import PasswordStrengthMeter, { isPasswordValid } from '../components/PasswordStrengthMeter';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
};

const DemoInviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validateError, setValidateError] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      try {
        const res = await axios.get(`${API}/demo-invite/${token}`);
        setInvite(res.data);
      } catch (err) {
        setValidateError(formatErr(err?.response?.data?.detail) || err.message);
      } finally {
        setValidating(false);
      }
    };
    if (token) run();
    else { setValidating(false); setValidateError('Missing invite token.'); }
  }, [token]);

  const passwordOk = useMemo(() => isPasswordValid(password), [password]);
  const matches = password.length > 0 && password === confirm;
  const canSubmit = passwordOk && matches && !submitting && invite;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordOk) { setError('Your password does not meet the strength policy.'); return; }
    if (!matches) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/demo-invite/${token}/accept`,
        { password },
        { withCredentials: true },
      );
      setUser(res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="demo-invite-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (validateError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="demo-invite-error-page">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Invite invalid
          </h1>
          <p className="text-sm text-slate-600 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {validateError} If you need a new link, start a free trial directly or contact your account manager.
          </p>
          <Link to="/start-trial" data-testid="demo-invite-request-new" className="inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white py-2.5 px-5 rounded-md font-semibold text-sm hover:bg-[#00247D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Start a free trial
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="demo-invite-accept-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>

          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Activate your account
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Set a password for <strong>{invite.email}</strong> at <strong>{invite.company_name}</strong>. Your <strong>14-day free trial</strong> starts the moment you click below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="demo-invite-form">
            {error && (
              <div data-testid="demo-invite-form-error" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{error}</div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={reveal ? 'text' : 'password'}
                  required
                  autoFocus
                  data-testid="demo-invite-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
                <button type="button" onClick={() => setReveal((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                  {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} testid="demo-invite-password-strength" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Confirm password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={reveal ? 'text' : 'password'}
                  required
                  data-testid="demo-invite-confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={submitting}
                  className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
              </div>
              {confirm.length > 0 && !matches && (
                <p className="text-xs text-red-600 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Passwords do not match.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="demo-invite-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white py-3 px-4 rounded-md font-semibold hover:bg-[#00247D] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 focus:outline-none transition-all disabled:opacity-50"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Activating…' : 'Activate & start trial'}
              {!submitting && <CheckCircle2 className="w-4 h-4" />}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative" style={{ backgroundImage: 'url(/images/login-hero.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/40 to-transparent" />
      </div>
    </div>
  );
};

export default DemoInviteAccept;
